import { useCallback } from 'react'
import { usePinchZoom } from './usePinchZoom'
import { useSingleTouchDrag } from './useSingleTouchDrag'

interface ViewboxPosition {
  x: number
  y: number
}

interface TouchHandlingConfig {
  dragFactor: number
  onViewboxChange: (x: number, y: number) => void
  onZoomChange: (zoom: number) => void
  currentViewbox: ViewboxPosition
}

interface TouchHandlingReturn {
  onTouchStart: (touchEvent: React.TouchEvent) => void
  onTouchMove: (touchEvent: React.TouchEvent) => void
  onTouchEnd: () => void
}

export const useTouchHandling = ({
  dragFactor,
  onViewboxChange,
  onZoomChange,
  currentViewbox
}: TouchHandlingConfig): TouchHandlingReturn => {
  const { handlePinchZoom, resetPinchZoom } = usePinchZoom({ onZoomChange })
  
  const { 
    handleSingleTouchDrag, 
    initializeTouchDrag, 
    resetTouchDrag 
  } = useSingleTouchDrag({
    dragFactor,
    onViewboxChange,
    currentViewbox
  })

  const onTouchStart = useCallback(
    (touchEvent: React.TouchEvent) => {
      resetPinchZoom()
      initializeTouchDrag(touchEvent)
    },
    [resetPinchZoom, initializeTouchDrag]
  )

  const onTouchMove = useCallback(
    (touchEvent: React.TouchEvent) => {
      if (touchEvent.touches.length === 2) {
        handlePinchZoom(touchEvent)
        return
      }

      if (touchEvent.touches.length === 1) {
        handleSingleTouchDrag(touchEvent)
      }
    },
    [handlePinchZoom, handleSingleTouchDrag]
  )

  const onTouchEnd = useCallback(() => {
    resetPinchZoom()
    resetTouchDrag()
  }, [resetPinchZoom, resetTouchDrag])

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  }
}